import { PLAN_STATE, type PlanState, UserRoles, useSubscriptionInfo, useTranslation } from "@next-platform/client-common-core";
import { IconVariants } from "@next-platform/client-common-spicy-ui";
import { useMemo } from "react";

import { getPlanDisplayColors, type PlanColorTokens } from "@/constants/planTierColors";
import { PLAN_STATE_STATUS, resolvePeriodLabel, resolveStatusLabel } from "@/utils/planStateLabels";

type CurrentPlanViewModel = {
  planState: PlanState;
  planName: string;
  statusLabel: string;
  periodLabel: string;
  manageLabel: string;
  upgradeLabel: string;
  shouldShowManage: boolean;
  shouldShowUpgrade: boolean;
  colors: PlanColorTokens;
  statusIcon: IconVariants | null;
};

const useCurrentPlan = (): CurrentPlanViewModel => {
  const { t } = useTranslation();
  const { planState, displayPlanRole, displayPlanName, shouldShowManage, formattedExpiryDate, daysRemaining, formattedRenewsDate, hasRenewsDate } =
    useSubscriptionInfo();
  const hasVisibleExpiryDate = formattedExpiryDate.length > 0;

  return useMemo(() => {
    const statusIcon = PLAN_STATE_STATUS[planState];
    const colors = getPlanDisplayColors({ role: displayPlanRole, planState });

    const manageLabel = t("user:profile.currentPlan.manage");

    const isFreePlan = planState === PLAN_STATE.FREE;
    const isFreePlanRole = displayPlanRole === UserRoles.FREE;
    const resolvedPlanName = isFreePlanRole ? t("user:profile.currentPlan.freePlan.title") : displayPlanName;

    return {
      planState,
      planName: resolvedPlanName,
      statusLabel: resolveStatusLabel({ planState, t }),
      periodLabel: resolvePeriodLabel({
        planState,
        hasExpiryDate: hasVisibleExpiryDate,
        formattedExpiryDate,
        daysRemaining,
        t,
        hasRenewsDate,
        formattedRenewsDate,
      }),
      manageLabel,
      upgradeLabel: t("user:profile.currentPlan.upgrade"),
      shouldShowManage: !isFreePlan && shouldShowManage,
      shouldShowUpgrade: isFreePlan,
      colors,
      statusIcon,
    };
  }, [
    daysRemaining,
    displayPlanName,
    displayPlanRole,
    formattedExpiryDate,
    formattedRenewsDate,
    hasRenewsDate,
    hasVisibleExpiryDate,
    planState,
    shouldShowManage,
    t,
  ]);
};

export default useCurrentPlan;
